import { z } from 'zod';

export const uuidSchema = z.string().uuid();

export const idParamSchema = z.object({ id: uuidSchema });

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(200).optional(),
  sort: z.string().trim().max(60).optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
  });
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

export const okSchema = z.object({ ok: z.literal(true) });

/**
 * A yes/no query-string flag. `z.coerce.boolean()` must not be used for these: it turns the
 * text "false" into true, so `?unreadOnly=false` would mean the opposite of what it says.
 */
export const queryBooleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((v) => v === true || v === 'true' || v === '1');

export const timestampsSchema = z.object({
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** Trimmed, non-empty display name. */
export const nameSchema = z.string().trim().min(1).max(160);
export const optionalTextSchema = z.string().trim().max(2000).optional().nullable();
export const tagsSchema = z.array(z.string().trim().min(1).max(40)).max(30).default([]);
